import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

const WEEK_CHOICES = [1, 2, 3, 4, 5, 6, 7, 8];

export function JourneyBuilder({ open, onOpenChange, companyId, journey, onSaved }) {
  const editing = !!journey;
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [allCategories, setAllCategories] = useState([]);
  const [selectedCats, setSelectedCats] = useState(new Set());
  const [scenariosByCat, setScenariosByCat] = useState({});
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [weekOf, setWeekOf] = useState({}); // scenarioId -> week number
  const [titleById, setTitleById] = useState({}); // scenarioId -> title (for the organize list)
  const [saving, setSaving] = useState(false);

  // Load categories once
  useEffect(() => {
    if (!open) return;
    api.get("/admin/categories").then((r) => setAllCategories(r.data.categories || [])).catch(() => {});
  }, [open]);

  // Prefill when editing (or reset when creating)
  useEffect(() => {
    if (!open) return;
    if (journey) {
      setName(journey.name || "");
      setDescription(journey.description || "");
      const cats = new Set(journey.categories || []);
      setSelectedCats(cats);
      const ids = new Set((journey.scenario_ids || []).map(String));
      setSelectedIds(ids);
      const wo = {};
      (journey.weeks || []).forEach((w) => (w.scenario_ids || []).forEach((sid) => { wo[String(sid)] = w.week; }));
      setWeekOf(wo);
      cats.forEach((c) => loadCategory(c));
    } else {
      setName("");
      setDescription("");
      setSelectedCats(new Set());
      setSelectedIds(new Set());
      setWeekOf({});
      setScenariosByCat({});
      setTitleById({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, journey]);

  const loadCategory = async (cat) => {
    if (scenariosByCat[cat]) return;
    try {
      const r = await api.get(`/admin/scenarios-by-category?category=${encodeURIComponent(cat)}`);
      const list = r.data.scenarios || [];
      setScenariosByCat((prev) => ({ ...prev, [cat]: list }));
      setTitleById((prev) => {
        const next = { ...prev };
        list.forEach((s) => { next[s.id] = s.title; });
        return next;
      });
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const toggleCat = (cat) => {
    setSelectedCats((prev) => {
      const next = new Set(prev);
      if (next.has(cat)) next.delete(cat);
      else { next.add(cat); loadCategory(cat); }
      return next;
    });
  };

  const toggleScenario = (id, checked) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
    setWeekOf((prev) => {
      const next = { ...prev };
      if (checked && !next[id]) next[id] = 1;
      if (!checked) delete next[id];
      return next;
    });
  };

  const save = async () => {
    if (!name.trim()) { toast.error("Give the journey a name"); return; }
    if (selectedIds.size === 0) { toast.error("Select at least one scenario"); return; }
    const ids = [...selectedIds];
    const weeksMap = {};
    ids.forEach((id) => {
      const w = weekOf[id] || 1;
      (weeksMap[w] = weeksMap[w] || []).push(id);
    });
    const weeks = Object.keys(weeksMap)
      .map(Number)
      .sort((a, b) => a - b)
      .map((w) => ({ week: w, title: `Week ${w}`, scenario_ids: weeksMap[w] }));
    const payload = {
      name: name.trim(),
      description: description.trim(),
      categories: [...selectedCats],
      scenario_ids: ids,
      weeks,
    };
    setSaving(true);
    try {
      if (editing) {
        await api.patch(`/admin/companies/${companyId}/journeys/${journey.id}`, payload);
        toast.success("Journey updated");
      } else {
        await api.post(`/admin/companies/${companyId}/journeys`, payload);
        toast.success("Journey created");
      }
      onOpenChange(false);
      onSaved && onSaved();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const selectedList = [...selectedIds];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-2xl max-w-2xl max-h-[88vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display">{editing ? "Edit journey" : "Create learning journey"}</DialogTitle></DialogHeader>
        <div className="space-y-6 mt-2" data-testid="journey-builder">
          {/* Step 1: name */}
          <div>
            <Label className="text-xs text-stone-600 mb-1.5 block">Journey name *</Label>
            <Input data-testid="journey-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Leadership Fast Track" className="h-10" />
            <Input data-testid="journey-desc-input" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Short description (optional)" className="h-10 mt-2" />
          </div>

          {/* Step 2: categories */}
          <div>
            <div className="text-xs tracking-[0.16em] uppercase text-stone-500 mb-2">1 · Select categories</div>
            <div className="grid grid-cols-2 gap-2">
              {allCategories.map((c) => (
                <label key={c} className="flex items-center gap-2 text-sm text-stone-700 bg-stone-50 border border-stone-200 rounded-lg px-3 py-2 cursor-pointer">
                  <Checkbox data-testid={`journey-cat-${c}`} checked={selectedCats.has(c)} onCheckedChange={() => toggleCat(c)} />
                  <span className="truncate">{c}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Step 3: scenarios per category */}
          {[...selectedCats].length > 0 && (
            <div>
              <div className="text-xs tracking-[0.16em] uppercase text-stone-500 mb-2">2 · Pick scenarios</div>
              <div className="space-y-4">
                {[...selectedCats].map((c) => (
                  <div key={c}>
                    <div className="text-sm font-medium text-stone-800 mb-1.5">{c}</div>
                    <div className="space-y-1.5">
                      {(scenariosByCat[c] || []).map((s) => (
                        <label key={s.id} className="flex items-center gap-2 text-sm text-stone-600 pl-1">
                          <Checkbox data-testid={`journey-scenario-${s.id}`} checked={selectedIds.has(s.id)} onCheckedChange={(v) => toggleScenario(s.id, !!v)} />
                          <span>{s.title}</span>
                        </label>
                      ))}
                      {!scenariosByCat[c] && <div className="text-xs text-stone-400 pl-1">Loading…</div>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Step 4: organize into weeks */}
          {selectedList.length > 0 && (
            <div>
              <div className="text-xs tracking-[0.16em] uppercase text-stone-500 mb-2">3 · Organize into weeks</div>
              <div className="space-y-2" data-testid="journey-weeks">
                {selectedList.map((id) => (
                  <div key={id} className="flex items-center justify-between gap-3 bg-white border border-stone-200 rounded-lg px-3 py-2">
                    <span className="text-sm text-stone-700 truncate">{titleById[id] || id}</span>
                    <select
                      data-testid={`journey-week-of-${id}`}
                      value={weekOf[id] || 1}
                      onChange={(e) => setWeekOf((prev) => ({ ...prev, [id]: parseInt(e.target.value, 10) }))}
                      className="h-8 rounded-md border border-stone-200 bg-white px-2 text-sm"
                    >
                      {WEEK_CHOICES.map((w) => <option key={w} value={w}>Week {w}</option>)}
                    </select>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        <DialogFooter className="mt-4">
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button data-testid="save-journey-btn" onClick={save} disabled={saving} className="bg-[#b81f24] hover:bg-[#9a1a1f] text-white">
            {saving ? "Saving…" : editing ? "Save changes" : "Create journey"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
